// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SITE_ROLLOUT_FLAG, type SiteRollout } from '../config/launchDarklyFlags';

/**
 * A dashboard typo must not empty the app: anything off-shape reads as the
 * default, the whole value at once. Dropping only the bad members of `sites`
 * would turn `[123]` into an explicit empty rollout and hide every site.
 */
function parse(value: unknown): SiteRollout {
  if (typeof value !== 'object' || value === null) return SITE_ROLLOUT_FLAG.default;
  const { all, sites } = value as { all?: unknown; sites?: unknown };
  if (typeof all !== 'boolean' || !Array.isArray(sites)) return SITE_ROLLOUT_FLAG.default;
  if (!sites.every(id => typeof id === 'string')) return SITE_ROLLOUT_FLAG.default;
  return { all, sites };
}

/** Typed read of the `site-rollout` JSON flag (config/launchDarklyFlags.ts). */
export function useSiteRollout(): SiteRollout {
  const raw = useFlags()[SITE_ROLLOUT_FLAG.key];
  return useMemo(() => parse(raw), [raw]);
}
